import type { Player } from "../../../common/types.ts";
import { PHASE, PLAYER } from "../../../common/constants.ts";
import { g, helpers } from "../../util/index.ts";
import { idb } from "../../db/index.ts";
import moodInfo from "../player/moodInfo.ts";
import genContract from "../player/genContract.ts";
import { teamBaseballBudget } from "../team/aiFinances.baseball.ts";
import { organizationPlayers } from "./organizationPlayers.ts";
import { tradeDisposition } from "./disposition.ts";
import { majorLeagueBenchmarks, minorLeagueTradeFactor, minorsStage } from "../player/minorsSatisfaction.ts";
const bound = (n: number, low: number, high: number) =>
	Math.max(low, Math.min(high, n));

export const loadContractTeamContext = async (tid: number) => {
	const season = g.get("season");
	const t = await idb.cache.teams.get(tid);
	if (!t) throw new Error("Invalid contract-context team");
	const seasons = await idb.cache.teamSeasons.indexGetAll(
		"teamSeasonsByTidSeason",
		[
			[tid, season - 1],
			[tid, season],
		],
	);
	const current = seasons.find((row) => row.season === season);
	const previous = seasons.find((row) => row.season === season - 1);
	const gp = current ? helpers.getTeamSeasonGp(current) : 0;
	const oldWinp =
		previous && helpers.getTeamSeasonGp(previous) > 0
			? helpers.calcWinp(previous)
			: 0.5;
	const winp =
		(gp * (current ? helpers.calcWinp(current) : 0.5) + 40 * oldWinp) /
		(gp + 40);
	const phase = g.get("phase");
	const seasonRemaining =
		phase === PHASE.PRESEASON
			? 1
			: phase <= PHASE.AFTER_TRADE_DEADLINE
				? bound(1 - gp / Math.max(1, g.get("numGames")), 0.02, 1)
				: phase === PHASE.PLAYOFFS
					? 0.08
					: 0;
	return {
		tid,
		season,
		seasonRemaining,
		players: await organizationPlayers(tid),
		minorBenchmarks: majorLeagueBenchmarks(await idb.cache.players.indexGetAll("playersByTid", [0, Infinity])),
		budget: await teamBaseballBudget(tid),
		strategy: t.strategy,
		winp,
		contenderStrength:
			t.strategy === "contending" ? bound((winp - 0.35) / 0.2, 0, 1) : 0,
	};
};
export type ContractTeamContext = Awaited<
	ReturnType<typeof loadContractTeamContext>
>;
export const contractContext = async (p: Player, t: ContractTeamContext, competitiveIndex = t.contenderStrength) => {
	const age = t.season - p.born.year;
	const yearsRemaining = Math.max(
		0,
		p.contract.exp - t.season + t.seasonRemaining,
	);
	// Existing commitments that survive into this player's renewal season. Minor salaries
	// follow the game's payroll exemption; this is a budget estimate, not a cap exception.
	const commitments = t.players
		.filter(
			(other) =>
				other.pid !== p.pid &&
				other.tid !== PLAYER.MINORS &&
				other.contract.exp > p.contract.exp,
		)
		.reduce((sum, other) => sum + other.contract.amount, 0);
	const available = Math.max(0, t.budget - commitments);
	const expectedSalary = genContract(p, false).amount;
 const mood = await moodInfo(p, t.tid, {contractAmount: expectedSalary, salaryCoverage: available / Math.max(1, expectedSalary)});
 const result = tradeDisposition({age, yearsRemaining, renewalInterest: mood.renewalInterest,
 components: mood.components, traits: g.get("playerMoodTraits") ? p.moodTraits : [], competitiveIndex,
 tradeRequest: (p.tid === PLAYER.MINORS ? p.minorTid : p.tid) === t.tid && (minorsStage(p)?.min ?? 0) >= 50});

	return {
		...result,
		opportunityFactor: minorLeagueTradeFactor(p, t.minorBenchmarks),
  renewalScore: mood.interestScore,
  factors: [{label:"Interesse da negociação / FA (mood, traits, carreira)", value:mood.interestScore},
   {label:"Capacidade financeira",value:mood.financialInterest}],
		expectedSalary,
		availableSalary: available,
		commitments,
		budget: t.budget,
		contextTid: t.tid,
	};
};
export type ContractContext = Awaited<ReturnType<typeof contractContext>>;

